import * as React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import { visuallyHidden } from "@mui/utils";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faClose,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import ExtremeHeavyRainfallChartOverlayer from "../ExtremeHeavyRainfallChartOverlayer/ExtremeHeavyRainfallChartOverlayer";
import ChartFilter from "../ExtremeHeavyRainfallChartOverlayer/ChartFilter/ChartFilter";
import { useState } from "react";

interface Data {
  sensorID: number;
  sensorName: string;
  thirtyMinutes: string;
  oneHour: string;
  twelveHours: string;
  twentyFourHours: string;
  earlyWarningStatus: string;
}

function createData(
  sensorID: number,
  sensorName: string,
  thirtyMinutes: string,
  oneHour: string,
  twelveHours: string,
  twentyFourHours: string,
  earlyWarningStatus: string
): Data {
  return {
    sensorID,
    sensorName,
    thirtyMinutes,
    oneHour,
    twelveHours,
    twentyFourHours,
    earlyWarningStatus,
  };
}

const rows = [
  createData(
    1,
    "Kelani",
    "95.20 (5.00%)",
    "95.90 (5.20%)",
    "100.10 (10.91%)",
    "98.60 (12.50%)",
    "Triggered"
  ),
  createData(
    2,
    "Kukuleganga",
    "108.00 (11.21%)",
    "109.90 (15.30%)",
    "108.50 (9.90%)",
    "115.10 (6.20%)",
    "Triggered"
  ) /* 
  createData(
    3,
    "Kukuleganga Rainfall Sensor",
    "108.00",
    "109.90",
    "112.00",
    "115.10",
    "Triggered"
  ), */,
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  [x: string]: any;
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "sensorName",
    numeric: false,
    disablePadding: false,
    label: "Sensor Name",
  },
  {
    id: "thirtyMinutes",
    numeric: false,
    disablePadding: false,
    label: "30 Minutes (mm)",
  },
  {
    id: "oneHour",
    numeric: false,
    disablePadding: false,
    label: "1 Hour (mm)",
  },
  {
    id: "twelveHours",
    numeric: false,
    disablePadding: false,
    label: "12 Hours (mm)",
  },
  {
    id: "twentyFourHours",
    numeric: false,
    disablePadding: false,
    label: "24 Hours (mm)",
  },
  {
    id: "earlyWarningStatus",
    numeric: false,
    disablePadding: false,
    label: "Early Warning Status",
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead sx={{}}>
      <TableRow sx={{}}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
      
              style={{ color: "white" }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const handleExportRows = (rows: any) => {
    const doc = new jsPDF();
    const tableData = rows.map((row: Data) => {
      const { sensorID, ...dataWithoutID } = row;
      return Object.values(dataWithoutID);
    });
    const tableHeaders = headCells
      .filter((c) => c.id !== "sensorID")
      .map((c) => c.label);

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save("Heavy-Rainfall-Report.pdf");
  };
  const { numSelected } = props;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        maxHeight: "34px",
        ...(numSelected > 0 && {
          bgcolor: (theme: {
            palette: {
              primary: { main: string };
              action: { activatedOpacity: number };
            };
          }) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{}} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ color: "white" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {/* Heavy Rainfall */}
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton onClick={() => handleExportRows(rows)} onTouchStart={() => handleExportRows(rows)}>
            <Download />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}
export default function ExtremeHeavyRainfallTable() {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("sensorName");
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [rowClicked, setRowClicked] = React.useState<boolean>(false);
  const [leftWidth, setLeftWidth] = React.useState<number>(90);
  const [resizedChart, setResizedChart] = React.useState<boolean>(false);
  const [chartTitle, setChartTitle] = React.useState<string>("");
  const [showChartFilter, setShowChartFilter] = React.useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState("thirtymin");

  const handleTimeRangeChange = (newTimeRange: string) => {
    setSelectedTimeRange(newTimeRange);
  
  };

  const handleCloseChartFilter = () => {
    setShowChartFilter(false);
  };

  const handleMouseDown = React.useCallback(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const newLeftWidth = (event.clientX / window.innerWidth) * 100;
      setLeftWidth(newLeftWidth);
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setResizedChart(true);
  
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, []);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n: any) => n.hazardID);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    setRowClicked(true);
  };

  const handleTouchStart = (event: React.TouchEvent<unknown>, id: number) => {
    setRowClicked(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage]
  );

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "2px",
      }}
    >
      <div
        style={{
          height: "100%",
          width: rowClicked ? `${leftWidth}%` : "100%",
          borderRadius: "2px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            //height: "100%",
            width: "100%",
            borderRadius: "2px",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
          }}
        >
          <EnhancedTableToolbar numSelected={selected.length} />
          <TableContainer
            sx={{
              overflowY: "auto",
              height: "200px",
            }}
          >
            <Table sx={{}} aria-labelledby="tableTitle" size={"small"}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.sensorID);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => {
                        handleClick(event, row.sensorID);
                        setChartTitle(row.sensorName);
                      }}
                      onTouchStart={(event) => {
                        handleTouchStart(event, row.sensorID);
                        setChartTitle(row.sensorName);
                      }}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.sensorID}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer", color: "white", height: 23 }}
                    >
                      <TableCell
                        style={{
                          color: "white",
                        }}
                        align="left"
                      >
                        {row.sensorName}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          color: "white",
                        }}
                      >
                        {row.thirtyMinutes} <>&nbsp;</>
                        <FontAwesomeIcon
                          style={{
                            color: "red",
                          }}
                          icon={faCaretUp}
                        />
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          color: "white",
                        }}
                      >
                        {row.oneHour} <>&nbsp; </>
                        {
                          <FontAwesomeIcon
                            style={{
                              color:
                                Number(row.oneHour.split(" ")[0]) <
                                Number(row.thirtyMinutes.split(" ")[0])
                                  ? "#27D507"
                                  : "red",
                            }}
                            icon={
                              Number(row.oneHour.split(" ")[0]) <
                              Number(row.thirtyMinutes.split(" ")[0])
                                ? faCaretDown
                                : faCaretUp
                            }
                          />
                        }
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          color: "white",
                        }}
                      >
                        {row.twelveHours}
                        <>&nbsp; </>
                        {
                          <FontAwesomeIcon
                            style={{
                              color:
                                Number(row.twelveHours.split(" ")[0]) <
                                Number(row.oneHour.split(" ")[0])
                                  ? "#27D507"
                                  : "red",
                            }}
                            icon={
                              Number(row.twelveHours.split(" ")[0]) <
                              Number(row.oneHour.split(" ")[0])
                                ? faCaretDown
                                : faCaretUp
                            }
                          />
                        }
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          color: "white",
                        }}
                      >
                        {row.twentyFourHours}
                        <>&nbsp; </>
                        {
                          <FontAwesomeIcon
                            style={{
                              color:
                                Number(row.twentyFourHours.split(" ")[0]) <
                                Number(row.twelveHours.split(" ")[0])
                                  ? "#27D507"
                                  : "red",
                            }}
                            icon={
                              Number(row.twentyFourHours.split(" ")[0]) <
                              Number(row.twelveHours.split(" ")[0])
                                ? faCaretDown
                                : faCaretUp
                            }
                          />
                        }
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          color: "white",
                        }}
                      >
                        {row.earlyWarningStatus}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 23 : 23) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
        <div
          style={{
            width: "100%",
            padding: "10px 0",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            style={{
              height: "25px",
              width: "120px",
              border: "1px",
              borderRadius: "5px",
              color: "white",
              backgroundColor: "#008F11",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Analyse
          </button>
        </div>
      </div>
      {rowClicked && (
        <div
          style={{
            height: "95%",
            width: "5px",
            cursor: "ew-resize",
            backgroundColor: "rgba(85, 84, 95, 0.4)",
            zIndex: "999",
            margin: "5px",
          }}
          onMouseDown={handleMouseDown}
        ></div>
      )}
      {rowClicked && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: `${100 - leftWidth}%`,
          }}
        >
          <div
            style={{
              margin: "10px 0",
              borderRadius: "2px",
              width: "100%",
              height: "100%",
            }}
          >
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(85, 84, 95, 0.4)",
                padding: "0.1px 0px",
                borderRadius: "2px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  margin: "5px",
                }}
              >
                <FontAwesomeIcon
                  onClick={() => setShowChartFilter(true)}
                  onTouchStart={() => setShowChartFilter(true)}
                  style={{
                    cursor: "pointer",
                    color: "white",
                    borderRadius: "50%",
                    margin: "0 10px 0 0px",
                    width: "15px",
                    height: "15px",
                  }}
                  icon={faFilter}
                />
                <FontAwesomeIcon
                  onClick={() => setRowClicked(false)}
                  onTouchStart={() => setRowClicked(false)}
                  style={{
                    cursor: "pointer",
                    color: "red",
                    border: "3px solid #dc143c",
                    borderRadius: "50%",
                    width: "15px",
                    height: "15px",
                  }}
                  icon={faClose}
                />
              </div>
            </div>
            {showChartFilter && (
              <ChartFilter onClose={handleCloseChartFilter} onTimeRangeChange={handleTimeRangeChange} />
            )}
            <ExtremeHeavyRainfallChartOverlayer
              onClose={() => setRowClicked(false)}
              resizeChart={leftWidth}
              chartTitle={chartTitle}
              selectTime={selectedTimeRange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
